import { lazy, type ReactNode } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { BoardShell } from '@/layout/BoardShell';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { AuthGate } from '@/auth/AuthGate';
import { featureEnabled, setupPageEnabled } from '@/config/features';
import { ATTENDANCE_ROUTE, CONDUCT_ROUTE, COURSES_ROUTE, DOCUMENT_LIBRARY_ROUTE, LECTURE_LIBRARY_ROUTE, QUESTION_BANK_ROUTE } from '@/home/navigation';

// delivered by the whiteboard track
const WhiteboardPage = lazy(() => import('@/whiteboard/WhiteboardPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const HomePage = lazy(() => import('@/pages/HomePage'));
const SetupPage = lazy(() => import('@/pages/SetupPage'));
const LectureLibraryPage = lazy(() => import('@/pages/LectureLibraryPage'));
const QuestionBankPage = lazy(() => import('@/pages/QuestionBankPage'));
const ConductPage = lazy(() => import('@/pages/ConductPage'));
const AttendancePage = lazy(() => import('@/pages/AttendancePage'));
const CoursesPage = lazy(() => import('@/pages/CoursesPage'));
const DocumentLibraryPage = lazy(() => import('@/pages/DocumentLibraryPage'));
// dev-only harness: PROD is a build-time literal, so the gallery chunk is dropped from a production bundle
const PreviewPage = import.meta.env.PROD ? NotFoundPage : lazy(() => import('@/dev/PreviewPage'));

const guarded = (element: ReactNode) => <AuthGate>{element}</AuthGate>;

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <NotFoundPage /> },
  { path: '/', element: guarded(<HomePage />), errorElement: <NotFoundPage /> },
  { path: LECTURE_LIBRARY_ROUTE, element: guarded(<LectureLibraryPage />), errorElement: <NotFoundPage /> },
  { path: QUESTION_BANK_ROUTE, element: guarded(<QuestionBankPage />), errorElement: <NotFoundPage /> },
  { path: CONDUCT_ROUTE, element: guarded(<ConductPage />), errorElement: <NotFoundPage /> },
  { path: ATTENDANCE_ROUTE, element: guarded(<AttendancePage />), errorElement: <NotFoundPage /> },
  { path: COURSES_ROUTE, element: guarded(<CoursesPage />), errorElement: <NotFoundPage /> },
  { path: DOCUMENT_LIBRARY_ROUTE, element: guarded(<DocumentLibraryPage />), errorElement: <NotFoundPage /> },
  {
    path: '/board/:date/:index/:classId',
    element: guarded(<BoardShell />),
    errorElement: <NotFoundPage />,
    children: [
      { index: true, element: <WhiteboardPage /> },
    ]
  },
  // with SSO the IZ session comes from the QLT login, so an old bookmark to /setup just goes home
  { path: '/setup', element: setupPageEnabled() ? guarded(<SetupPage />) : featureEnabled('sso') ? <Navigate to="/" replace={true} /> : <NotFoundPage /> },
  { path: '/preview', element: featureEnabled('ops.preview') ? <PreviewPage /> : <NotFoundPage /> },
  { path: '/preview/:id', element: featureEnabled('ops.preview') ? <PreviewPage /> : <NotFoundPage /> },
  { path: '*', element: <NotFoundPage /> },
], { future: { v7_relativeSplatPath: true } });
